/** Formatting utilities (dates, URLs, text). */

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/** Parse the bookmark `tags` column (JSON array-of-strings as a string).
 *  Garbage — legacy raw strings, non-arrays, non-string members — yields []. */
export function parseTags(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

/** Format Unix epoch as relative time ("2h ago", "3d ago"). */
export function formatRelativeTime(epoch: number | null | undefined): string {
  if (!epoch) return '';
  const diff = Math.floor(Date.now() / 1000) - epoch;
  if (diff < 0) return 'just now';
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < WEEK) return `${Math.floor(diff / DAY)}d ago`;
  if (diff < MONTH) return `${Math.floor(diff / WEEK)}w ago`;
  if (diff < YEAR) return `${Math.floor(diff / MONTH)}mo ago`;
  return `${Math.floor(diff / YEAR)}y ago`;
}

/** Format Unix epoch as exact date (e.g., "2026-04-03 14:30"). */
export function formatExactDate(epoch: number | null | undefined): string {
  if (!epoch) return '';
  const d = new Date(epoch * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format Unix epoch as date-only group header (e.g., "Apr 3, 2026"). */
export function formatDateGroup(epoch: number | null | undefined): string {
  if (!epoch) return 'Unknown date';
  return new Date(epoch * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Truncate URL for display (strip protocol, trailing slash). */
export function formatUrl(url: string, maxLength = 60): string {
  let display = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (display.length > maxLength) {
    display = display.substring(0, maxLength - 1) + '\u2026';
  }
  return display;
}
